import { ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { DialogButton, PaginationPageEvent } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { SortDirection } from 'src/app/modules/pos/quotes/entities/sort.enum';
import { AccountsListService } from '../../accounts/services/accounts-list.service';
import { TouchpointsService } from '../services/touchpoints.service';
import { OutlookTouchpoint } from '../entities/outlook-touchpoint';
import { OutlookContact } from '../entities/outlook-contact';
import { OutlookTemplateValues } from '../entities/outlook-template-values.class';
import { ContactsSortBy } from '../entities/contacts-sort-by.class';
import { MergeTouchpointModalComponent } from '../merge-touchpoint-modal/merge-touchpoint-modal.component';
import { MergeContactModalComponent } from '../merge-contact-modal/merge-contact-modal.component';
import { touchpointTypeColors, touchpointTypeMaterialIcons } from '../entities/touchpoints-entity';

@Component({
  selector: 'app-manage-contacts-touchpoints',
  templateUrl: './manage-contacts-touchpoints.component.html',
  styleUrls: ['./manage-contacts-touchpoints.component.scss'],
  standalone: false
})
export class ManageContactsTouchpointsComponent implements OnInit {
  appStrings: AppLocalization;
  touchpointsList: OutlookTouchpoint[] = [];
  contactsList: OutlookContact[] = [];
  private unsortedTouchpointsList: OutlookTouchpoint[] = [];
  lnStatuses: { [key: number]: string } = {};

  touchpointColumns: string[] = ['type', 'subject', 'company', 'date', 'actions'];
  contactColumns: string[] = ['firstName', 'lastName', 'company', 'actions'];

  pageSize = 16;
  touchpointsPage = 1;
  contactsPage = 1;

  dateTimeFormat = this.appInfoService.dateTimeFormat;
  templateValues: any;
  amEmployeeId: string;

  touchpoints = { loading: true };
  contacts = { loading: false };

  deleteOpen$ = new EventEmitter<boolean>();
  deleteDialogTitle = '';
  deleteDialogDescription = '';
  deleteDialogButtons: DialogButton[] = [];

  private contactsSortBy: ContactsSortBy;

  constructor(
    private appInfoService: AppInfoService,
    private touchpointsService: TouchpointsService,
    private accountsListService: AccountsListService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private ref: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.templateValues = this.touchpointsService.templateValues;
    this.amEmployeeId = this.appInfoService.userData.UserInfo.EmployeeId;
    this.accountsListService.getTemplateValues(templateValues => {
      this.lnStatuses = { ...templateValues.LnStatus };
    });
    this.initData();
  }

  initData() {
    this.getTouchpointsList();
    this.getContactsList();
    this.touchpointsService.getOutlookTemplateValues().subscribe((values: OutlookTemplateValues) => {
      this.contactsSortBy = values.ContactSortBy;
    });
  }

  getTouchpointsList() {
    this.touchpoints.loading = true;
    this.touchpointsService.getCalendar().subscribe((response: OutlookTouchpoint[]) => {
      this.touchpointsList = response;
      this.unsortedTouchpointsList = response;
      this.touchpoints.loading = false;
    });
  }

  getContactsList(sort?: Sort) {
    if (!sort) {
      this.contacts.loading = true;
    }
    if (sort?.direction) {
      sort.active = Object.keys(this.contactsSortBy).find(
        sortKey => this.contactsSortBy[sortKey].toLowerCase() === sort.active.toLowerCase());
    }
    this.touchpointsService.getContacts(sort).subscribe((response: OutlookContact[]) => {
      this.contactsList = response;
      this.contacts.loading = false;
    });
  }

  get paginatedTouchpoints(): OutlookTouchpoint[] {
    const start = (this.touchpointsPage - 1) * this.pageSize;
    return this.touchpointsList.slice(start, start + this.pageSize);
  }

  get paginatedContacts(): OutlookContact[] {
    const start = (this.contactsPage - 1) * this.pageSize;
    return this.contactsList.slice(start, start + this.pageSize);
  }

  get touchpointsTotalPages(): number {
    return Math.ceil(this.touchpointsList.length / this.pageSize);
  }

  get contactsTotalPages(): number {
    return Math.ceil(this.contactsList.length / this.pageSize);
  }

  get touchpointsPageEnd(): number {
    return Math.min(this.touchpointsPage * this.pageSize, this.touchpointsList.length);
  }

  get contactsPageEnd(): number {
    return Math.min(this.contactsPage * this.pageSize, this.contactsList.length);
  }

  onTouchpointsPageChange(event: PaginationPageEvent) {
    this.touchpointsPage = event.pageNumber;
  }

  onTouchpointsSortChange(sort: Sort) {
    if (!sort.direction) {
      this.touchpointsList = this.unsortedTouchpointsList;
      return;
    }
    const field = sort.active as keyof OutlookTouchpoint;
    const sorted = [...this.unsortedTouchpointsList].sort(
      (a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? '')));
    if (SortDirection[sort.direction] === SortDirection.asc) {
      this.touchpointsList = sorted;
    } else {
      sorted.reverse();
      this.touchpointsList = sorted;
    }
  }

  onContactsPageChange(event: PaginationPageEvent) {
    this.contactsPage = event.pageNumber;
  }

  getTouchpointColor(touchpointType: number): string {
    return touchpointTypeColors[touchpointType] ?? '';
  }

  getTouchpointIcon(touchpointType: number): string {
    return touchpointTypeMaterialIcons[touchpointType] ?? '';
  }

  openMergeTouchpointModal(touchpoint: OutlookTouchpoint, event: Event) {
    event.stopPropagation();
    this.dialog.open(MergeTouchpointModalComponent, {
      width: '500px',
      panelClass: ['ds', 'fullWidth'],
      data: { touchpoint, amEmployeeId: this.amEmployeeId, lnStatuses: this.lnStatuses }
    });
  }

  openMergeContactModal(contact: OutlookContact, event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(MergeContactModalComponent, {
      width: '500px',
      panelClass: ['ds', 'fullWidth'],
      data: { contact, amEmployeeId: this.amEmployeeId, lnStatuses: this.lnStatuses }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.merged) {
        this.getContactsList();
      }
    });
  }

  deleteTouchpoint(touchpoint: OutlookTouchpoint) {
    const touchpointInfo = touchpoint.subject ? ` (${touchpoint.subject})` : '';
    this.deleteDialogTitle = `${this.appStrings.touchpoints_deleteTouchpointTitle}${touchpointInfo}`;
    this.deleteDialogDescription = this.appStrings.touchpoints_deleteConfirmation;
    this.deleteDialogButtons = [
      { label: this.appStrings.button_cancel, type: 'secondary', onclick: () => { } },
      {
        label: this.appStrings.button_delete, type: 'primary', onclick: () => {
          this.touchpointsService.deleteOutlookTouchpoint(touchpoint.id).subscribe(() => {
            this.notificationService.showNotification(this.appStrings.touchpoints_removedNotification, NotificationType.Warning, true);
            this.getTouchpointsList();
          });
        }
      }
    ];
    this.ref.detectChanges();
    this.deleteOpen$.emit(true);
  }

  deleteContact(contact: OutlookContact) {
    const contactFullName = this.getFullContactName(contact);
    this.deleteDialogTitle = `${this.appStrings.contacts_deleteContactTitle} ${contactFullName}`;
    this.deleteDialogDescription = this.appStrings.contacts_deleteConfirmation;
    this.deleteDialogButtons = [
      { label: this.appStrings.button_cancel, type: 'secondary', onclick: () => { } },
      {
        label: this.appStrings.button_delete, type: 'primary', onclick: () => {
          this.touchpointsService.deleteOutlookContact(contact.id, () => {
            this.getContactsList();
          });
        }
      }
    ];
    this.ref.detectChanges();
    this.deleteOpen$.emit(true);
  }

  getFullContactName(contact: OutlookContact): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  }
}
